import { useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import type { DataTableColumn } from '@/components/common/DataTable';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type {
  CreateWeightProductionIssueRequest,
  WeightProductionIssueDto,
  WeightProductionIssueLineInput,
  WeightProductionPickListDto,
} from '@/types/weightProduction';

interface LineState {
  qty: number;
  scannedBarcode: string;
  /** Barcode-wise mode only — true once this line's own physical label has been scanned through the
   * shared Scan Barcode field (see tryMatchScan). Drives both submit-readiness and the
   * scanned-rows-to-top ordering. */
  checked: boolean;
}

/**
 * Consumes the input materials a BOM-based Weigh & Label plan owes, so the plan can be weighed.
 *
 * Deliberately the same shape as Dispatch Material Issue, down to the scan flow: with Barcode
 * Required on, WeightProductionPickListService generates one pick list line per physical InStock
 * label, so each row here is exactly one unit — scan it, it floats to the top and is issued in full;
 * nothing to split or partially confirm. With Barcode Required off, rows are quantity-wise and the
 * barcode UI is hidden entirely.
 */
export default function WeightProductionIssuePage() {
  const { t } = useTranslation('weightProduction');
  const queryClient = useQueryClient();

  const { data: pickLists = [] } = useQuery({
    queryKey: ['weight-production-pick-lists'],
    queryFn: async () => (await apiClient.get<WeightProductionPickListDto[]>('/weight-production-pick-lists')).data,
  });
  const { data: issues = [], isLoading: loadingIssues } = useQuery({
    queryKey: ['weight-production-issues'],
    queryFn: async () => (await apiClient.get<WeightProductionIssueDto[]>('/weight-production-issues')).data,
  });

  const { data: config } = useProcessConfiguration();
  const barcodeMandatory = config?.barcodeMandatory ?? false;

  // Material Issue only confirms what has already been picked — it never resolves stock itself, so
  // a plan with no pick list, or one already fully issued, has nothing to do here.
  const openPickLists = pickLists.filter((p) => p.status !== 'Completed');

  const [pickListId, setPickListId] = useState('');
  const [lineState, setLineState] = useState<Record<string, LineState>>({});
  // One shared scan input rather than a field per line: a scanner operator holds the gun, not the
  // mouse, and should never have to click into the right box first.
  const [scanInput, setScanInput] = useState('');
  const [scanOrder, setScanOrder] = useState<string[]>([]);

  const selectedPickList = openPickLists.find((p) => p.id === pickListId) ?? null;

  /** What actually gates a real issue for this line — the resolved location when there is one, else
   * the whole-warehouse total. */
  const availableQtyFor = (line: { locationCode: string | null; warehouseAvailableQty: number; locationAvailableQty: number }) =>
    line.locationCode ? line.locationAvailableQty : line.warehouseAvailableQty;

  const selectPickList = (id: string) => {
    setPickListId(id);
    setScanOrder([]);
    setScanInput('');
    const pickList = openPickLists.find((p) => p.id === id);
    setLineState(
      Object.fromEntries(
        (pickList?.lines ?? []).map((l) => {
          const remaining = Math.max(l.requiredQty - l.pickedQty, 0);
          // Defaults to what is actually on the shelf when that is short of what was allocated — the
          // operator can still post the short issue and the rest follows when stock arrives.
          const qty = Math.max(Math.min(remaining, availableQtyFor(l)), 0);
          return [l.id, { qty, scannedBarcode: '', checked: !barcodeMandatory }];
        }),
      ),
    );
  };

  // Matches on every keystroke (a scanner types the whole value near-instantly) and again on Enter,
  // for a gun that sends one or for manual typing.
  const tryMatchScan = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed || !selectedPickList) {
      return false;
    }
    const matchLine = selectedPickList.lines.find(
      (l) => !lineState[l.id]?.checked && (l.barcode ?? '').toUpperCase() === trimmed,
    );
    if (!matchLine) {
      return false;
    }
    setLineState((prev) => ({ ...prev, [matchLine.id]: { ...prev[matchLine.id], checked: true, scannedBarcode: value.trim() } }));
    setScanOrder((prev) => [matchLine.id, ...prev.filter((id) => id !== matchLine.id)]);
    setScanInput('');
    return true;
  };

  const handleScanInputChange = (value: string) => {
    if (!tryMatchScan(value)) {
      setScanInput(value);
    }
  };

  const handleScanKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryMatchScan(scanInput);
    }
  };

  /**
   * Rows grouped by material, because barcode-wise picking turns one material into many one-unit
   * rows — six labels here would otherwise read as six unrelated lines, and "how many Stabilizer
   * Links are left?" could only be answered by counting.
   *
   * Group ORDER is deliberately stable: sections that reorder as you scan make the operator chase a
   * moving target. Within a group, scanned rows float to the top (most recent first) so what is
   * still outstanding is always the part you are looking at.
   */
  const materialGroups = useMemo(() => {
    if (!selectedPickList) {
      return [];
    }
    const groups = new Map<string, { materialId: string; materialCode: string; materialName: string; lines: typeof selectedPickList.lines }>();

    for (const line of selectedPickList.lines) {
      const existing = groups.get(line.materialId);
      if (existing) {
        existing.lines.push(line);
      } else {
        groups.set(line.materialId, {
          materialId: line.materialId,
          materialCode: line.materialCode,
          materialName: line.materialName,
          lines: [line],
        });
      }
    }

    return [...groups.values()].map((group) => {
      const scannedFirst = scanOrder
        .map((id) => group.lines.find((l) => l.id === id))
        .filter((l): l is NonNullable<typeof l> => Boolean(l));
      const rest = group.lines.filter((l) => !scanOrder.includes(l.id));
      const ordered = barcodeMandatory ? [...scannedFirst, ...rest] : group.lines;

      const countable = group.lines.filter((l) => (lineState[l.id]?.qty ?? 0) > 0 && l.barcode);
      return {
        ...group,
        lines: ordered,
        scannedCount: countable.filter((l) => lineState[l.id]?.checked).length,
        totalCount: countable.length,
        requiredQty: group.lines.reduce((sum, l) => sum + Math.max(l.requiredQty - l.pickedQty, 0), 0),
      };
    });
  }, [selectedPickList, scanOrder, lineState, barcodeMandatory]);

  const issueMutation = useMutation({
    mutationFn: () => {
      const payload: CreateWeightProductionIssueRequest = {
        weightProductionOrderId: selectedPickList?.weightProductionOrderId ?? '',
        remarks: null,
        lines: Object.entries(lineState)
          .filter(([, s]) => s.qty > 0)
          .map(([pickListLineId, s]): WeightProductionIssueLineInput => ({
            pickListLineId,
            issuedQty: s.qty,
            scannedBarcode: s.scannedBarcode || null,
          })),
      };
      return apiClient.post('/weight-production-issues', payload);
    },
    onSuccess: () => {
      // The plan's issued totals move, and it may now be weighable at the kiosk.
      queryClient.invalidateQueries({ queryKey: ['weight-production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['weight-production-pick-lists'] });
      queryClient.invalidateQueries({ queryKey: ['weight-production-issues'] });
      setPickListId('');
      setLineState({});
    },
  });

  const totalToIssue = useMemo(
    () => Object.values(lineState).reduce((sum, s) => sum + (s.qty > 0 ? s.qty : 0), 0),
    [lineState],
  );

  const allBarcodesReady = useMemo(() => {
    if (!selectedPickList) {
      return false;
    }
    if (!barcodeMandatory) {
      return true;
    }
    return selectedPickList.lines.every((l) => {
      const s = lineState[l.id];
      if (!s || s.qty <= 0) {
        return true;
      }
      return l.barcode ? s.checked : false;
    });
  }, [selectedPickList, lineState, barcodeMandatory]);

  // The server rejects an over-issue outright, so it is caught here too — better a disabled button
  // than a submission that bounces.
  const hasOverStockLine = useMemo(() => {
    if (!selectedPickList) {
      return false;
    }
    return selectedPickList.lines.some((l) => {
      const s = lineState[l.id];
      return Boolean(s) && s.qty > availableQtyFor(l);
    });
  }, [selectedPickList, lineState]);

  const historyColumns: DataTableColumn<WeightProductionIssueDto>[] = [
    { field: 'issueNumber', headerName: t('issue.columns.issueNumber') },
    { field: 'orderNumber', headerName: t('issue.columns.plan'), filterable: true },
    { field: 'warehouseName', headerName: t('issue.columns.warehouse'), filterable: true },
    { field: 'issueDate', headerName: t('issue.columns.date'), render: (r) => new Date(r.issueDate).toLocaleString() },
    { field: 'issuedByUserName', headerName: t('issue.columns.issuedBy'), render: (r) => r.issuedByUserName ?? '—' },
    { field: 'lines', headerName: t('issue.columns.lines'), render: (r) => r.lines.length },
  ];

  return (
    <>
      <PageHeader title={t('issue.title')} subtitle={t('issue.subtitle')} />

      <Box sx={{ maxWidth: 480, mx: { xs: 0, sm: 'auto' } }}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <SearchableSelect
                label={t('issue.selectPickList')} value={pickListId} onChange={selectPickList} fullWidth size="medium"
                options={openPickLists.map((p) => ({ value: p.id, label: `${p.pickListNumber} — ${p.orderNumber}` }))}
              />

              {/* The plan's own target zone — where this material is headed once produced, not
                  where these input lines are being picked FROM. Shown so the operator issuing
                  stock knows what the resulting output is destined for. */}
              {selectedPickList && (selectedPickList.orderZoneCode || selectedPickList.orderZoneName) && (
                <Chip
                  size="small"
                  label={t('issue.destinationZone', {
                    zone: selectedPickList.orderZoneName
                      ? `${selectedPickList.orderZoneName} / ${selectedPickList.orderZoneCode}`
                      : selectedPickList.orderZoneCode,
                  })}
                  sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
                />
              )}

              {openPickLists.length === 0 && (
                <Typography variant="body2" color="text.secondary">{t('issue.nothingPending')}</Typography>
              )}

              {selectedPickList && barcodeMandatory && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <TextField
                    label={t('issue.scanBarcode')}
                    value={scanInput}
                    onChange={(e) => handleScanInputChange(e.target.value)}
                    onKeyDown={handleScanKeyDown}
                    helperText={t('issue.scanBarcodeHelper')}
                    fullWidth
                    autoFocus
                  />
                  <Chip
                    label={t('issue.scannedOfTotal', {
                      scanned: selectedPickList.lines.filter((l) => (lineState[l.id]?.qty ?? 0) > 0 && l.barcode && lineState[l.id]?.checked).length,
                      total: selectedPickList.lines.filter((l) => (lineState[l.id]?.qty ?? 0) > 0 && l.barcode).length,
                    })}
                    color={allBarcodesReady ? 'success' : 'warning'}
                    sx={{ mt: 1.25, fontWeight: 700, minWidth: 92, flexShrink: 0 }}
                  />
                </Stack>
              )}

              {selectedPickList && materialGroups.map((group) => (
                <Stack key={group.materialId} spacing={1}>
                  {/* One heading per material — the answer to "how much of this is left" without
                      counting individual label rows. */}
                  <Stack
                    direction="row" alignItems="center" justifyContent="space-between"
                    sx={{ px: 1, py: 0.75, borderRadius: 1, bgcolor: 'action.hover' }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>{group.materialCode}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{group.materialName}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {barcodeMandatory && group.totalCount > 0 && (
                        <Chip
                          size="small"
                          label={t('issue.scannedOfTotal', { scanned: group.scannedCount, total: group.totalCount })}
                          color={group.scannedCount === group.totalCount ? 'success' : 'warning'}
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                      <Chip size="small" variant="outlined" label={t('issue.required')+`: ${group.requiredQty}`} />
                    </Stack>
                  </Stack>

                  {group.lines.map((line) => {
                    const s = lineState[line.id];
                    const remaining = Math.max(line.requiredQty - line.pickedQty, 0);
                    const available = availableQtyFor(line);
                    const overStock = Boolean(s) && s.qty > available;
                    const scanned = barcodeMandatory && Boolean(s?.checked) && (s?.qty ?? 0) > 0;
                    return (
                      <Box
                        key={line.id}
                        sx={{
                          p: 1.5, border: 1, borderRadius: 1,
                          borderColor: scanned ? 'success.main' : 'divider',
                          bgcolor: scanned ? 'success.50' : undefined,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            {barcodeMandatory && line.barcode && (
                              // Read-only: confirming a line is scan-driven only, never a click.
                              <Checkbox size="small" checked={Boolean(s?.checked)} disabled sx={{ mt: -0.5, ml: -1 }} />
                            )}
                            <Box>
                              {/* Code and name live on the group heading now — repeating them per
                                  label row would bury the detail that differs: location and batch. */}
                              <Typography variant="caption" display="block" color="text.secondary">
                                {line.locationCode ? `${t('issue.location')}: ${line.locationCode} · ` : ''}
                                {line.batchNo ? `${t('issue.batch')}: ${line.batchNo} · ` : ''}
                                {t('issue.required')}: {remaining}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {t('issue.warehouseStock', { qty: line.warehouseAvailableQty })}
                                {line.locationCode ? ` · ${t('issue.locationStock', { qty: line.locationAvailableQty })}` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip size="small" label={t('issue.remaining', { qty: remaining })} color={remaining > 0 ? 'warning' : 'success'} />
                        </Stack>

                        {barcodeMandatory && (
                          line.barcode ? (
                            <Typography
                              variant="caption" display="block" sx={{ mb: 1.5 }}
                              color={s?.checked ? 'success.main' : 'text.secondary'}
                            >
                              {t('issue.expectedBarcode', { barcode: line.barcode })}
                              {s?.checked ? ` — ${t('issue.scanned')}` : ''}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="error" display="block" sx={{ mb: 1.5 }}>
                              {t('issue.noBarcodeBlocked')}
                            </Typography>
                          )
                        )}

                        <TextField
                          type="number" label={t('issue.qtyToIssue')} fullWidth size="medium"
                          value={s?.qty ?? 0}
                          error={overStock}
                          helperText={overStock ? t('issue.overStock', { qty: available }) : undefined}
                          onChange={barcodeMandatory
                            ? undefined
                            : (e) => setLineState((prev) => ({ ...prev, [line.id]: { ...prev[line.id], qty: Number(e.target.value) } }))}
                          slotProps={{
                            htmlInput: {
                              inputMode: 'decimal',
                              style: { fontSize: '1.1rem' },
                              // A scanned physical unit is issued in full — there is nothing to edit.
                              readOnly: barcodeMandatory,
                              max: available,
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              ))}

              <Divider />

              <Button
                variant="contained" size="large" fullWidth
                disabled={!pickListId || totalToIssue <= 0 || !allBarcodesReady || hasOverStockLine || issueMutation.isPending}
                onClick={() => issueMutation.mutate()}
              >
                {issueMutation.isPending ? t('issue.issuing') : t('issue.postIssue')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 1.5 }}>{t('issue.history')}</Typography>
        <DataTable columns={historyColumns} rows={issues} loading={loadingIssues} searchable getRowId={(r) => r.id} exportFileName="weigh-label-issues" />
      </Box>
    </>
  );
}
